import { computed, Injectable, signal } from '@angular/core';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { GeolocationPositionBasic, GpsCoordinate, GpsStepItem, MapPoint, ReferencePoint } from '@cue/api';

@Injectable()
export class GpsLocationService {
  private static readonly MIN_HEADING_MOVEMENT_METERS = 0.8;
  private static readonly MIN_HEADING_MOVEMENT_METERS_SIMULATOR = 0.05;
  private static readonly MIN_SPEED_FOR_NATIVE_HEADING_MPS = 0.4;
  private static readonly HEADING_DEADBAND_DEGREES = 1.2;
  private static readonly MAX_HEADING_STEP_DEGREES = 15;
  private static readonly ROTATION_DEADBAND_RADIANS = (1.2 * Math.PI) / 180;
  private static readonly MAX_ROTATION_STEP_RADIANS = (15 * Math.PI) / 180;
  private static readonly MIN_MAP_DELTA_UNITS = 0.2;
  private static readonly MIN_MAP_DELTA_UNITS_SIMULATOR = 0.01;
  private static readonly LOCATION_ANIMATION_BASE_MS = 120;
  private static readonly LOCATION_ANIMATION_PER_UNIT_MS = 4;
  private static readonly LOCATION_ANIMATION_MAX_MS = 550;
  private static readonly LOCATION_ANIMATION_EPSILON = 0.05;

  private watchId: number | null = null;
  private previousGpsSample?: { lat: number; lng: number };
  private previousMapSample?: MapPoint;
  private mapLocationAnimationFrame?: number;

  public isInitialized = signal(false);
  private origin: GpsCoordinate | null = null;
  transform = signal<{ scaleX: number; scaleY: number; offsetX: number; offsetY: number } | null>(null);

  startStopGpsSimulator = signal(false);
  currentSimulationGpsPosition = signal<GpsStepItem | null>(null);

  currentGpsPosition = computed<GeolocationPositionBasic | null>(() => {
    if (this.startStopGpsSimulator()) {
      const latitude = this.toFiniteNumber(this.currentSimulationGpsPosition()?.coords.lat);
      const longitude = this.toFiniteNumber(this.currentSimulationGpsPosition()?.coords.lng);
      if (latitude === null || longitude === null) {
        return null;
      }
      const heading = this.toFiniteNumber(this.currentSimulationGpsPosition()?.rotation);
      return {
        coords: {
          latitude,
          longitude,
          heading: heading ?? undefined,
        },
      };
    }
    return this.currentLocationReal();
  });

  started = false;
  private currentLocationReal = signal<GeolocationPositionBasic | null>(null);
  currentHeading = signal<number | null>(null);
  currentMapRotation = signal<number | null>(null);
  currentAreaMapLocation = signal<MapPoint | null>(null);

  private currentAreaMapLocationTarget = computed(() => {
    const gpsPosition = this.currentGpsPosition();
    if (gpsPosition) {
      return this.gpsToMapAffine({ lat: gpsPosition.coords.latitude, lng: gpsPosition.coords.longitude });
    }
    return null;
  });

  animateCurrentAreaMapLocation = explicitEffect([this.currentAreaMapLocationTarget], ([target]) => {
    this.animateMapLocationTo(target);
  });

  resetHeadingEstimatorOnSourceChange = explicitEffect([this.startStopGpsSimulator], () => {
    this.cancelMapLocationAnimation();
    this.previousGpsSample = undefined;
    this.previousMapSample = undefined;
    this.currentHeading.set(null);
    this.currentMapRotation.set(null);
    this.currentAreaMapLocation.set(null);
  });

  recalculateHeadingFromMovement = explicitEffect([this.currentGpsPosition], ([gpsPosition]) => {
    if (!gpsPosition) {
      return;
    }

    const lat = this.toFiniteNumber(gpsPosition.coords.latitude);
    const lng = this.toFiniteNumber(gpsPosition.coords.longitude);
    if (lat === null || lng === null) {
      return;
    }

    const isSimulator = this.startStopGpsSimulator();
    const minHeadingMovementMeters = isSimulator
      ? GpsLocationService.MIN_HEADING_MOVEMENT_METERS_SIMULATOR
      : GpsLocationService.MIN_HEADING_MOVEMENT_METERS;
    const minMapDeltaUnits = isSimulator
      ? GpsLocationService.MIN_MAP_DELTA_UNITS_SIMULATOR
      : GpsLocationService.MIN_MAP_DELTA_UNITS;

    const simulatedHeading = this.startStopGpsSimulator()
      ? this.toFiniteNumber(this.currentSimulationGpsPosition()?.rotation ?? gpsPosition.coords.heading)
      : null;
    const movementHeading = this.previousGpsSample ? this.calculateBearing(this.previousGpsSample, { lat, lng }) : null;
    const movementMeters = this.previousGpsSample
      ? this.calculateDistanceMeters(this.previousGpsSample, { lat, lng })
      : 0;
    const nativeHeading = !this.startStopGpsSimulator() ? this.toFiniteNumber(gpsPosition.coords.heading) : null;
    const nativeSpeed = this.toFiniteNumber(gpsPosition.coords.speed);

    let headingCandidate: number | null = null;
    if (simulatedHeading !== null) {
      headingCandidate = simulatedHeading;
    } else if (movementHeading !== null && movementMeters >= minHeadingMovementMeters) {
      headingCandidate = movementHeading;
    } else if (
      nativeHeading !== null &&
      (nativeSpeed === null || nativeSpeed >= GpsLocationService.MIN_SPEED_FOR_NATIVE_HEADING_MPS)
    ) {
      headingCandidate = nativeHeading;
    }

    if (headingCandidate !== null) {
      this.currentHeading.set(this.smoothHeading(this.normalizeHeading(headingCandidate)));
    }

    const currentMapPoint = this.gpsToMapAffine({ lat, lng });
    let mapRotationCandidate: number | null = null;
    if (currentMapPoint && this.previousMapSample) {
      const dx = currentMapPoint.x - this.previousMapSample.x;
      const dy = currentMapPoint.y - this.previousMapSample.y;
      const mapDistance = Math.hypot(dx, dy);
      if (mapDistance >= minMapDeltaUnits && movementMeters >= minHeadingMovementMeters) {
        // Angle of movement in map coordinates; rotate map so movement points to top.
        const movementAngle = Math.atan2(dy, dx);
        mapRotationCandidate = this.normalizeRotationRadians(movementAngle - Math.PI / 2);
      }
    }

    if (mapRotationCandidate === null && headingCandidate !== null) {
      mapRotationCandidate = this.degreesToRadians(-headingCandidate);
    }
    if (mapRotationCandidate !== null) {
      this.currentMapRotation.set(this.smoothRotation(this.normalizeRotationRadians(mapRotationCandidate)));
    }

    this.previousGpsSample = { lat, lng };
    if (currentMapPoint) {
      this.previousMapSample = currentMapPoint;
    }
  });

  private transformMatrix: {
    A: number;
    B: number;
    C: number;
    D: number;
    E: number;
    F: number;
  } | null = null;

  startWatchingLocation(): void {
    if (this.started) return;
    if (!navigator.geolocation) {
      console.error('Geolokace není podporována tímto prohlížečem.');
      return;
    }

    this.started = true;
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.currentLocationReal.set(position);
      },
      (error) => {
        console.error('Chyba při sledování polohy:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }

  stopWatchingLocation(): void {
    if (!this.started) return;
    this.started = false;
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.currentLocationReal.set(null);
    this.cancelMapLocationAnimation();
    this.previousGpsSample = undefined;
    this.previousMapSample = undefined;
    this.currentHeading.set(null);
    this.currentMapRotation.set(null);
    this.currentAreaMapLocation.set(null);
  }

  setReferencePointsAffine(referencePoints: [ReferencePoint, ReferencePoint, ReferencePoint]) {
    if (referencePoints.length !== 3) {
      throw new Error('Pro výpočet afinní transformace jsou potřeba přesně 3 referenční body.');
    }

    const [p1, p2, p3] = referencePoints;
    this.origin = p1.gps;

    // Převod GPS na lokální metrické souřadnice (v metrech)
    const toMeters = (gps: GpsCoordinate) => {
      const deltaLng = (gps.lng - p1.gps.lng) * Math.cos((p1.gps.lat * Math.PI) / 180);
      const deltaLat = gps.lat - p1.gps.lat;
      return {
        x: deltaLng * 111320, // délka v metrech
        y: deltaLat * 110540, // šířka v metrech
      };
    };

    const g1 = { x: 0, y: 0 }; // referenční bod 1 = počátek
    const g2 = toMeters(p2.gps);
    const g3 = toMeters(p3.gps);

    const m1 = p1.map;
    const m2 = p2.map;
    const m3 = p3.map;

    // Výpočet afinní transformace
    const denom = g1.x * (g2.y - g3.y) + g2.x * (g3.y - g1.y) + g3.x * (g1.y - g2.y);
    if (Math.abs(denom) < 1e-10) {
      throw new Error('Referenční body jsou kolineární – nelze vytvořit afinní transformaci.');
    }

    const A = (m1.x * (g2.y - g3.y) + m2.x * (g3.y - g1.y) + m3.x * (g1.y - g2.y)) / denom;
    const B = (m1.x * (g3.x - g2.x) + m2.x * (g1.x - g3.x) + m3.x * (g2.x - g1.x)) / denom;
    const C =
      (m1.x * (g2.x * g3.y - g3.x * g2.y) + m2.x * (g3.x * g1.y - g1.x * g3.y) + m3.x * (g1.x * g2.y - g2.x * g1.y)) /
      denom;

    const D = (m1.y * (g2.y - g3.y) + m2.y * (g3.y - g1.y) + m3.y * (g1.y - g2.y)) / denom;
    const E = (m1.y * (g3.x - g2.x) + m2.y * (g1.x - g3.x) + m3.y * (g2.x - g1.x)) / denom;
    const F =
      (m1.y * (g2.x * g3.y - g3.x * g2.y) + m2.y * (g3.x * g1.y - g1.x * g3.y) + m3.y * (g1.x * g2.y - g2.x * g1.y)) /
      denom;

    this.transformMatrix = { A, B, C, D, E, F };
    this.cancelMapLocationAnimation();
    this.previousGpsSample = undefined;
    this.previousMapSample = undefined;
    this.currentHeading.set(null);
    this.currentMapRotation.set(null);
    this.currentAreaMapLocation.set(null);
    this.isInitialized.set(true);
  }

  gpsToMapAffine(gps: GpsCoordinate): MapPoint | null {
    if (!this.origin || !this.transformMatrix) return null;

    const deltaLng = (gps.lng - this.origin.lng) * Math.cos((this.origin.lat * Math.PI) / 180);
    const deltaLat = gps.lat - this.origin.lat;
    const xMeters = deltaLng * 111320;
    const yMeters = deltaLat * 110540;

    const { A, B, C, D, E, F } = this.transformMatrix;

    const xMap = A * xMeters + B * yMeters + C;
    const yMap = D * xMeters + E * yMeters + F;

    return { x: xMap, y: yMap };
  }

  private smoothHeading(targetHeading: number): number {
    const currentHeading = this.currentHeading();
    if (currentHeading === null) {
      return targetHeading;
    }

    const delta = this.normalizeHeadingDelta(targetHeading - currentHeading);
    if (Math.abs(delta) <= GpsLocationService.HEADING_DEADBAND_DEGREES) {
      return currentHeading;
    }

    const boundedDelta = Math.max(
      -GpsLocationService.MAX_HEADING_STEP_DEGREES,
      Math.min(GpsLocationService.MAX_HEADING_STEP_DEGREES, delta)
    );
    return this.normalizeHeading(currentHeading + boundedDelta);
  }

  private smoothRotation(targetRotation: number): number {
    const currentRotation = this.currentMapRotation();
    if (currentRotation === null) {
      return targetRotation;
    }

    const delta = this.normalizeRotationRadians(targetRotation - currentRotation);
    if (Math.abs(delta) <= GpsLocationService.ROTATION_DEADBAND_RADIANS) {
      return currentRotation;
    }

    const boundedDelta = Math.max(
      -GpsLocationService.MAX_ROTATION_STEP_RADIANS,
      Math.min(GpsLocationService.MAX_ROTATION_STEP_RADIANS, delta)
    );
    return this.normalizeRotationRadians(currentRotation + boundedDelta);
  }

  private calculateBearing(from: GpsCoordinate, to: GpsCoordinate): number | null {
    const fromLatRad = this.degreesToRadians(from.lat);
    const toLatRad = this.degreesToRadians(to.lat);
    const deltaLonRad = this.degreesToRadians(to.lng - from.lng);

    const y = Math.sin(deltaLonRad) * Math.cos(toLatRad);
    const x =
      Math.cos(fromLatRad) * Math.sin(toLatRad) - Math.sin(fromLatRad) * Math.cos(toLatRad) * Math.cos(deltaLonRad);

    if (!Number.isFinite(x) || !Number.isFinite(y) || (Math.abs(x) < 1e-12 && Math.abs(y) < 1e-12)) {
      return null;
    }

    return this.normalizeHeading(this.radiansToDegrees(Math.atan2(y, x)));
  }

  private calculateDistanceMeters(from: GpsCoordinate, to: GpsCoordinate): number {
    const earthRadiusMeters = 6371000;
    const dLat = this.degreesToRadians(to.lat - from.lat);
    const dLng = this.degreesToRadians(to.lng - from.lng);
    const lat1 = this.degreesToRadians(from.lat);
    const lat2 = this.degreesToRadians(to.lat);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMeters * c;
  }

  private normalizeHeading(angle: number): number {
    const normalized = angle % 360;
    return normalized < 0 ? normalized + 360 : normalized;
  }

  private normalizeHeadingDelta(angle: number): number {
    let normalized = angle % 360;
    if (normalized > 180) {
      normalized -= 360;
    }
    if (normalized <= -180) {
      normalized += 360;
    }
    return normalized;
  }

  private toFiniteNumber(value: unknown): number | null {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private degreesToRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  private radiansToDegrees(radians: number): number {
    return (radians * 180) / Math.PI;
  }

  private normalizeRotationRadians(angle: number): number {
    const fullTurn = Math.PI * 2;
    let normalized = angle % fullTurn;
    if (normalized <= -Math.PI) {
      normalized += fullTurn;
    }
    if (normalized > Math.PI) {
      normalized -= fullTurn;
    }
    return normalized;
  }

  private animateMapLocationTo(target: MapPoint | null) {
    if (!target) {
      this.cancelMapLocationAnimation();
      this.currentAreaMapLocation.set(null);
      return;
    }

    const current = this.currentAreaMapLocation();
    if (!current) {
      this.currentAreaMapLocation.set(target);
      return;
    }

    const dx = target.x - current.x;
    const dy = target.y - current.y;
    const distance = Math.hypot(dx, dy);
    if (distance <= GpsLocationService.LOCATION_ANIMATION_EPSILON) {
      this.currentAreaMapLocation.set(target);
      return;
    }

    this.cancelMapLocationAnimation();
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      this.currentAreaMapLocation.set(target);
      return;
    }

    const duration = Math.max(
      GpsLocationService.LOCATION_ANIMATION_BASE_MS,
      Math.min(
        GpsLocationService.LOCATION_ANIMATION_MAX_MS,
        GpsLocationService.LOCATION_ANIMATION_BASE_MS + distance * GpsLocationService.LOCATION_ANIMATION_PER_UNIT_MS
      )
    );
    const startedAt = performance.now();
    const from = { ...current };

    const step = (timestamp: number) => {
      const progress = Math.min(1, (timestamp - startedAt) / duration);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.currentAreaMapLocation.set({
        x: from.x + dx * easedProgress,
        y: from.y + dy * easedProgress,
      });

      if (progress < 1) {
        this.mapLocationAnimationFrame = requestAnimationFrame(step);
      } else {
        this.mapLocationAnimationFrame = undefined;
        this.currentAreaMapLocation.set(target);
      }
    };

    this.mapLocationAnimationFrame = requestAnimationFrame(step);
  }

  private cancelMapLocationAnimation() {
    if (this.mapLocationAnimationFrame !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.mapLocationAnimationFrame);
      this.mapLocationAnimationFrame = undefined;
    }
  }
}
